import React, { useRef, useMemo, useState, useEffect } from 'react';
import classnames from 'classnames';
import { useIntl, getLocale } from 'umi';
import { Dropdown, Button, Select, InputAdornment, Input, Icon } from 'tea-component';
import useCloseWhenClickOutSide from './_hooks/useCloseWhenClickOutSide';

import './index.scss';

export interface IRevenueGrowthValue {
  opt: string;
  value: string | number;
}

interface IRevenueGrowthProps extends IBaseProps {
  isRevenueGrowthTwo?: boolean;
  buttonText?: string;
  placeholderText?: string;
  value: IRevenueGrowthValue;
  onChange: (value: IRevenueGrowthValue) => void;
}

const RevenueGrowth: React.FC<IRevenueGrowthProps> = (props) => {
  const {
    className,
    isRevenueGrowthTwo = false,
    buttonText = 'dbi_revenue_growth',
    placeholderText = 'dbi_input_growth',
    value,
    onChange,
  } = props;

  const locale = getLocale();
  const { formatMessage } = useIntl();

  const dropDown = useRef<HTMLDivElement>(null);
  const popover = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const [isOpen, setIsOpen] = useState(false);

  const [isError, setIsError] = useState(false);
  const [errorMsg, setErrorMsg] = useState('');

  const [optValue, setOptValue] = useState('');
  const [inputValue, setInputValue] = useState('');

  const options = [
    {
      text: formatMessage({ id: 'dbi_less_than' }),
      label: '<',
      value: '<',
    },
    {
      text: formatMessage({ id: 'dbi_less_than_equal' }),
      label: '≤',
      value: '<=',
    },
    {
      text: formatMessage({ id: 'dbi_greater_than' }),
      label: '>',
      value: '>',
    },
    {
      text: formatMessage({ id: 'dbi_greater_than_equal' }),
      label: '≥',
      value: '>=',
    },
    {
      text: formatMessage({ id: 'dbi_equal_to' }),
      label: '=',
      value: '=',
    },
  ];

  useCloseWhenClickOutSide({ dropDown, popover, setIsOpen, setIsError });

  const growthTitle = useMemo(() => {
    if (optValue && inputValue) {
      const optTitle = options?.find((item) => item?.value === optValue)?.label || '';
      return `${optTitle} ${inputValue}%`;
    }

    return (
      <span className="dbi_revenue-growth-select-input-range">
        {isRevenueGrowthTwo ? '' : formatMessage({ id: placeholderText })}
      </span>
    );
  }, [locale, isOpen, optValue, inputValue]);

  const handleSelectChange = (val: string) => {
    setOptValue(val);
    if (!val) {
      setIsError(false);
    }
  };

  const handleInputChange = (val: string) => {
    setInputValue(val);

    if (!val && !!optValue) {
      setIsError(true);
      setErrorMsg(`dbi_cannot_be_null`);
      return;
    }

    if (!/^[0-9]*[.]?[0-9]*$/.test(val) || (isRevenueGrowthTwo && parseInt(val, 10) > 100)) {
      setIsError(true);
      setErrorMsg('dbi_revenue_error_format_tip');
      return;
    }

    setIsError(false);
  };

  const handleDelete = () => {
    setInputValue('');
    setIsError(false);

    inputRef.current?.focus();
  };

  const handleConfirm = () => {
    setIsOpen(false);
    onChange({
      opt: optValue,
      value: inputValue ? Number(inputValue) : '',
    });
  };

  useEffect(() => {
    setInputValue(value?.value ? `${value?.value}` : '');
    setOptValue(value?.opt);
  }, [isOpen, value]);

  return (
    <Dropdown
      ref={dropDown}
      className={classnames('dbi-revenue-growth', { 'select-item': !isRevenueGrowthTwo }, className)}
      overlayClassName={classnames('dbi-revenue-growth-overlay', className)}
      matchButtonWidth
      appearance="button"
      clickClose={false}
      open={isOpen}
      // @ts-ignore
      onClick={() => setIsOpen(!isOpen)}
      button={
        <div className="dbi-revenue-growth-button">
          <span className="dbi-revenue-growth-value">{formatMessage({ id: buttonText })}</span>
          <span>{growthTitle}</span>
        </div>
      }
    >
      <div style={{ padding: 16 }} ref={popover}>
        <Select
          style={{ width: '100%' }}
          overlayClassName="dbi—revenue—growth-select-popover"
          appearance="button"
          clearable={true}
          matchButtonWidth={true}
          placeholder={formatMessage({ id: 'dbi_please_select' })}
          options={options}
          value={optValue}
          onChange={(value) => handleSelectChange(value)}
        />
        <InputAdornment style={{ display: 'flex', marginTop: 16 }} className="dbi-revenue-ment-box" before="%">
          <Input
            className={classnames('dbi-revenue-ment-input', { 'is-error': isError })}
            ref={inputRef}
            placeholder={formatMessage({ id: 'dbi_please_input' })}
            value={inputValue}
            onChange={(val) => handleInputChange(val)}
          />
          {inputValue && <Icon className="dbi-revenue-ment-icon" type="dismiss" onClick={() => handleDelete()} />}
        </InputAdornment>
        {isError && <div className="dbi-revenue-growth-select-input-error">{formatMessage({ id: errorMsg })}</div>}
        <div className="dbi-revenue-growth-btn">
          <Button
            className="dbi-revenue-growth-left"
            type="primary"
            disabled={isError || (!!optValue && !inputValue) || (!optValue && !!inputValue)}
            onClick={handleConfirm}
          >
            {formatMessage({ id: 'dbi_confirm' })}
          </Button>
          <Button
            className="dbi-revenue-growth-right"
            onClick={() => {
              setIsOpen(false);
              setIsError(false);
            }}
          >
            {formatMessage({ id: 'dbi_cancel' })}
          </Button>
        </div>
      </div>
    </Dropdown>
  );
};

export default RevenueGrowth;
